import React, { useState, useMemo } from "react";
import { useGetExpenses, useGetExpensesCategory } from "../../hooks/useExpenceApi";
import Container from "../../components/ui/Container";
import ExpensesList from "./ExpensesList";
import { useAuth } from "../../hooks/AuthContext";
import { ExpenseReportList } from "./ExpenceReportList";
import { RotateCcw } from "lucide-react";

export default function ExpenceReport() {
  const { user } = useAuth();
  const userId = user?.employeeProfile?._id;

  // 🔹 EMPLOYEE FILTERS (for employees only)
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // 🔹 Fetch categories
  const { data: categoriesData } = useGetExpensesCategory();
  const categories = categoriesData?.expenses || [];

  // 🔹 Employee expense filters
  const filters = useMemo(() => {
    const f = { page, limit };

    if (user?.role === "employee" && userId) f.employeeId = userId;
    if (searchFilter) f.search = searchFilter.trim();
    if (statusFilter) f.status = statusFilter;
    if (categoryFilter) f.categoryId = categoryFilter;
    if (fromDate)
      f.startDate = Math.floor(new Date(fromDate + "T00:00:00").getTime() / 1000);
    if (toDate)
      f.endDate = Math.floor(new Date(toDate + "T23:59:59").getTime() / 1000);

    return f;
  }, [page, limit, user?.role, userId, searchFilter, statusFilter, categoryFilter, fromDate, toDate]);

  // ✅ Fetch employee expenses
  const { data, isLoading } = useGetExpenses(filters, {
    enabled: !!user && (!!userId || user?.role !== "employee"),
  });

  const expenses = data?.expenses || [];
  const totalPages = data?.pages || Math.ceil((data?.total || 0) / limit);

  const onPageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Container>
      <h1 className="text-base font-bold mb-2">Expense Reports</h1>

      {/* ✅ EMPLOYEE FILTERS SECTION */}
      {user?.role === "employee" ? (
        <>
          <div className="mb-2 ">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">Search Expense</label>
                <input
                  type="text"
                  placeholder="Search by Category"
                  value={searchFilter}
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                    setPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                />
              </div>

              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                >
                  <option value="">All</option>
                  <option value="approve">Approved</option>
                  <option value="reject">Rejected</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                />
              </div>

              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm text-transparent mb-1 font-semibold select-none">Reset</label>
                <button
                  onClick={() => {
                    setSearchFilter("");
                    setCategoryFilter("");
                    setStatusFilter("");
                    setFromDate("");
                    setToDate("");
                    setPage(1);
                  }}
                  className="border px-2 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-gray-700"
                  title="Clear all filters"
                >
                  <RotateCcw size={17} />
                  <span className="text-xs">Reset</span>
                </button>
              </div>
            </div>
           
          </div>

          {/* Employee Expenses List */}
          <ExpensesList
            expenses={expenses}
            isLoading={isLoading}
            statusFilter={["pending","approve","reject"]}
            paginationData={{ page, pages: totalPages }}
            onPageChange={onPageChange}
          />
        </>
      ) : (
        /* ✅ ADMIN/MANAGER VIEW - Filters inside ExpenseReportList */
        <ExpenseReportList />
      )}
    </Container>
  );
}
